import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles } from "lucide-react";

/**
 * QuoteDialog — shared inbound-lead capture used by PricingSection
 * (per-tier CTA) and TeamSection (contact pill on desktop).
 * All submissions land in db.quote_requests and trigger a founder email.
 */
export const QuoteDialog = ({
  open,
  onOpenChange,
  plan,
  setPlan,
  title = "Request a custom quote",
  description = "Tell us a bit about your team — we'll send a tailored proposal within one business day.",
  messagePlaceholder = "e.g. Screen 500+ engineers/week and rank fairly across regions.",
}) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", team_size: "1-10", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quote-requests", { ...form, plan });
      toast.success("Thanks — we'll reach out within one business day");
      onOpenChange(false);
      setForm({ name: "", email: "", company: "", team_size: "1-10", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#06B6D4]" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Your name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="quote-name-input" className="bg-[#0B0F19] border-white/10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Work email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="quote-email-input" className="bg-[#0B0F19] border-white/10" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                     data-testid="quote-company-input" className="bg-[#0B0F19] border-white/10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Hiring team size</Label>
              <Select value={form.team_size} onValueChange={(v) => setForm({ ...form, team_size: v })}>
                <SelectTrigger data-testid="quote-team-size" className="bg-[#0B0F19] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10 text-slate-100">
                  <SelectItem value="1-10">1 – 10</SelectItem>
                  <SelectItem value="11-50">11 – 50</SelectItem>
                  <SelectItem value="51-200">51 – 200</SelectItem>
                  <SelectItem value="200+">200+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Plan you're interested in</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="quote-plan-select" className="bg-[#0B0F19] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 text-slate-100">
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="growth">Growth</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
                <SelectItem value="custom">Not sure — recommend for me</SelectItem>
                <SelectItem value="contact">General enquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">What are you hoping to solve? (optional)</Label>
            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder={messagePlaceholder}
                      data-testid="quote-message-input"
                      className="bg-[#0B0F19] border-white/10 text-sm" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="quote-submit-btn"
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6">
              {busy ? "Sending…" : "Send request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
